import * as React from 'react'
import cn from 'classnames'
import { sortBy } from 'src/design-system'
import styles from './HeadcountOverview.module.scss'
import { Team } from '../types'

export type HeadcountOverviewProps = {
  onSelect?: (teamId: number) => void
  teams: (Team & { selected?: boolean })[]
}

/**
 * Given a list of teams, show a bar graph detailing the number of members on each team.
 * Each bar is selectable and calls `onSelect` with the team name that the user clicks.
 */
export const HeadcountOverview: React.VFC<HeadcountOverviewProps> = ({
  onSelect,
  teams,
}) => {
  // REVISIT: we can avoid this work if we can guarantee
  // that the response from the server is always sorted.
  const sortedTeams = React.useMemo(
    () => sortBy(teams, 'numMembers').reverse(),
    [teams]
  )
  const maxMembers = sortedTeams.at(0)?.numMembers ?? 0

  return (
    <ol className={styles.container}>
      {teams.map((team) => (
        <li key={team.name}>
          <button
            aria-label={`Select ${team.name}`}
            className={cn(
              styles.barContainer,
              onSelect != null && styles.selectable
            )}
            disabled={onSelect == null}
            onClick={() => onSelect?.(team.id)}
          >
            <p className={styles.teamLabel}>{team.name}</p>
            <Bar
              className={cn(team.selected && styles.selected)}
              percentage={team.numMembers / maxMembers}
            />
            <p>{team.numMembers}</p>
          </button>
        </li>
      ))}
    </ol>
  )
}

const Bar: React.VFC<{ className?: string; percentage: number }> = ({
  className,
  percentage,
}) => (
  <div
    className={cn(styles.bar, className)}
    style={{
      width: `${(percentage * 100).toFixed(2)}%`,
    }}
  />
)
